import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Box, Button, Spinner, Stack, Text } from '@chakra-ui/react';
import { renderSVG } from 'uqr';
import type { Bridge } from '@casa/bridge';
import { deviceName, pollDeviceSignIn, startDeviceSignIn, type DeviceSignIn } from '../data/casa_server.ts';
import { ChromeDialog } from '../ui/chrome_dialog.tsx';
import { ChromeError } from '../ui/chrome_page.tsx';

// Signing this device in with a Tilez Cloud account (docs/specs/cloud-accounts.md AC-13, AC-14): it
// shows a short code and a QR code of the account page's link; on a phone or a computer, signed in
// (or after making an account), the user approves it there and picks the space. Meanwhile the device
// asks every few seconds whether it was approved.

function messageOf(error: unknown) : string {
    return error instanceof Error ? error.message : String(error);
}

/** `onSignedIn`: approved; `isNew` when the account put it in a new space, whose keys are to be made here. */
export function DeviceSignInDialog({ shell, onSignedIn, onClose }: { shell: Bridge; onSignedIn: (isNew: boolean) => void; onClose: () => void }) : ReactNode {
    const [started, setStarted] = useState<DeviceSignIn>();
    const [error, setError] = useState<string>();
    const [attempt, setAttempt] = useState(0);
    /** The attempt asked for already: React's strict mode runs an effect twice. */
    const asked = useRef(-1);

    useEffect(() => {
        if (asked.current === attempt) {
            return;
        }

        asked.current = attempt;
        setStarted(undefined);
        setError(undefined);

        let isCurrent = true;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const poll = async (signIn: DeviceSignIn) : Promise<void> => {
            try {
                const answer = await pollDeviceSignIn(shell, signIn.deviceCode);

                if (!isCurrent) {
                    return;
                }

                if (answer.status === 'waiting') {
                    timer = setTimeout(() => void poll(signIn), signIn.interval * 1000);
                } else if (answer.status === 'approved') {
                    onSignedIn(answer.isNew);
                } else {
                    setError(answer.message);
                }
            } catch (caught) {
                // A network hiccup: ask again, as the code is still good.
                if (isCurrent) {
                    timer = setTimeout(() => void poll(signIn), signIn.interval * 1000);
                    console.warn(`account sign-in: ${messageOf(caught)}`);
                }
            }
        };

        void (async () => {
            try {
                const signIn = await startDeviceSignIn(shell, deviceName(await shell.device.info()));

                if (isCurrent) {
                    setStarted(signIn);
                    timer = setTimeout(() => void poll(signIn), signIn.interval * 1000);
                }
            } catch (caught) {
                if (isCurrent) {
                    setError(messageOf(caught));
                }
            }
        })();

        return () => {
            isCurrent = false;

            if (timer !== undefined) {
                clearTimeout(timer);
            }
        };
        // onSignedIn is the caller's, called once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shell, attempt]);

    return (
        <ChromeDialog
            title="Sign in with your account"
            onClose={onClose}
            size="sm"
            footer={
                <>
                    {error ? (
                        <Button variant="outline" onClick={() => setAttempt((value) => value + 1)}>
                            Start again
                        </Button>
                    ) : null}
                    <Button variant="ghost" onClick={onClose}>
                        Cancel
                    </Button>
                </>
            }
        >
            {error ? (
                <ChromeError text={error} />
            ) : started ? (
                <Stack gap="4" align="center" textAlign="center">
                    <Text>On your phone or computer, scan this code or open the link, sign in, and choose this device's home.</Text>
                    <Box bg="white" p="2" borderRadius="md" width="44" height="44" data-testid="sign-in-qr" dangerouslySetInnerHTML={{ __html: renderSVG(started.verifyUrl, { border: 1 }) }} css={{ '& svg': { width: '100%', height: '100%' } }} />
                    <Text fontSize="sm" color="fg.muted" userSelect="text" wordBreak="break-all" data-testid="sign-in-url">
                        {started.verifyUrl.replace(/\?.*$/, '')}
                    </Text>
                    <Stack gap="0">
                        <Text fontSize="sm" color="fg.muted">
                            Your code
                        </Text>
                        <Text fontFamily="mono" fontSize="2xl" fontWeight="bold" data-testid="sign-in-code">
                            {started.userCode}
                        </Text>
                    </Stack>
                    <Text fontSize="sm" color="fg.muted">
                        No account yet? You can make one there first. Waiting for you…
                    </Text>
                    <Spinner size="sm" />
                </Stack>
            ) : (
                <Spinner />
            )}
        </ChromeDialog>
    );
}
